// SPDX-License-Identifier: GPL-3.0-or-later
// The score gate of a run. It scales each award by its channel, then pays through ScoreGate.
//
// The scale makes the ScoreGate queue hold more points. It never makes the pay rate higher.
// ScoreGate still pays at most SCORE_RATE_CAP_PER_SECOND in any window of 1000 ms.
// Pure: no I/O, no clock. Time comes in with the RulesContext.

import type { RulesContext } from "../../shared/contracts.ts";
import { ScoreGate } from "../rules/scorer.ts";
import { RUN_LIMITS } from "./perks.ts";
import type { RunPayer, RunRulesPort, ScoreChannel } from "./types.ts";

/** The lowest scale. A run never lowers an award. */
const MIN_SCALE = 1;

/** The highest scale. It is the bound of the perk spec. */
export const MAX_SCALE = RUN_LIMITS.MAX_CHANNEL_SCALE;

/** The part of the run that the gate reads. */
type RunScaler = Pick<RunRulesPort, "scaleFor" | "addStagePoints">;

/** The award reasons of vibe-01.ts and the channel of each. The run awards are in it too. */
const REASON_CHANNEL: ReadonlyMap<string, ScoreChannel> = new Map<string, ScoreChannel>([
  ["Slingshot", "switch"],
  ["Token Burn bumper", "switch"],
  ["Bot Spinner", "switch"],
  ["Bot Spinner (lit)", "switch"],
  ["Outlane", "switch"],
  ["Inlane", "switch"],
  ["Skill shot", "switch"],
  ["Top lane", "switch"],
  ["TOKENS target", "switch"],
  ["DENY target", "switch"],
  ["Ghost Ramp", "ramp"],
  ["Merge Ramp", "ramp"],
  ["Super skill shot", "ramp"],
  ["Bot Orbit", "orbit"],
  ["10X Orbit", "orbit"],
  ["Combo", "combo"],
  ["Merged Ramps", "combo"],
  ["SigmaBot Swarm: jackpot", "jackpot"],
  ["SigmaBot Swarm: super jackpot", "jackpot"],
  ["ASK Mode: DENY", "jackpot"],
  ["ASK Mode: Grand Approval", "jackpot"],
  ["Context Window Full: target", "mode"],
  ["Context Window Full: complete", "mode"],
  ["Accept All: flat bonus", "mode"],
  ["Accept All: complete", "mode"],
  ["Bot Orbit Run: complete", "mode"],
  ["Bot Friday Deploy: complete", "mode"],
  ["Lit shot", "event"],
  ["Hurry-up", "event"],
  ["Combo chain", "event"],
]);

/** The channel of an item, for a reason that the table does not know. */
const ITEM_CHANNEL: ReadonlyMap<string, ScoreChannel> = new Map<string, ScoreChannel>([
  ["RampLeftExit", "ramp"],
  ["RampRightExit", "ramp"],
  ["OrbitLeftTop", "orbit"],
  ["OrbitRightTop", "orbit"],
]);

/**
 * The channel of an award. The reason decides first. The item decides for an unknown reason.
 * Every other award is "other". No perk scales it.
 */
export function channelOf(reason: string, item?: string): ScoreChannel {
  const byReason = REASON_CHANNEL.get(reason);
  if (byReason) return byReason;
  return (item !== undefined ? ITEM_CHANNEL.get(item) : undefined) ?? "other";
}

/** A scale inside MIN_SCALE..MAX_SCALE. A scale that is not a number gives MIN_SCALE. */
export function clampScale(scale: number): number {
  if (!Number.isFinite(scale)) return MIN_SCALE;
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
}

export class RunGate extends ScoreGate implements RunPayer {
  constructor(
    capPerSecond: number,
    private readonly run: RunScaler,
  ) {
    super(capPerSecond);
  }

  /** Scale the award by its channel, count it in the stage, then pay through the base gate. */
  override award(ctx: RulesContext, points: number, reason: string, item?: string): void {
    this.pay(ctx, points, reason, channelOf(reason, item), item);
  }

  /**
   * Pay an award of a channel. The channel "event" is never scaled.
   * The run pays its own awards here.
   */
  pay(
    ctx: RulesContext,
    points: number,
    reason: string,
    channel: ScoreChannel,
    item?: string,
  ): void {
    if (!Number.isFinite(points) || points <= 0) return;
    const scale = channel === "event" ? MIN_SCALE : clampScale(this.run.scaleFor(channel));
    const scaled = Math.floor(points * scale);
    if (!Number.isFinite(scaled) || scaled <= 0) return;
    this.run.addStagePoints(ctx, scaled);
    super.award(ctx, scaled, reason, item);
  }
}
